import {
  Breadcrumb,
  Button,
  Form,
  FormProps,
  Input,
  Radio,
  Tag,
  notification,
} from "antd";
import { ArrowLeftOutlined, ReloadOutlined } from "@ant-design/icons";
import React, { Dispatch, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import {
  TemplateThunkType,
  getOneTemplate,
  updateTemplate,
} from "../store/actions/templateActions";
import { AnyAction } from "redux";
import { useDispatch, useSelector } from "react-redux";
import { AppStateType } from "../store";
import { templatePreviewApi } from "../api";

type FieldType = {
  html: string;
  styles: string;
  script: string;
};

export const Template = () => {
  const [form] = Form.useForm();

  const { TextArea } = Input;
  const dispatch = useDispatch() as Dispatch<TemplateThunkType | AnyAction>;
  let { id } = useParams();
  const template = useSelector(
    (state: AppStateType) => state.template.template
  );
  const isTemplateUpdateInProgress = useSelector(
    (state: AppStateType) => state.template.isTemplateUpdateInProgress
  );

  const [previewHtml, setPreviewHtml] = useState<string | null>(null);
  const [isPreviewLoading, setIsPreviewLoading] = useState(false);
  const [previewWidth, setPreviewWidth] = useState<"desktop" | "mobile">(
    "desktop"
  );

  const loadPreview = async (useFormValues: boolean) => {
    if (!id) {
      return;
    }
    try {
      setIsPreviewLoading(true);
      const body = useFormValues
        ? {
            templateId: id,
            html: form.getFieldValue("html"),
            styles: form.getFieldValue("styles"),
            script: form.getFieldValue("script"),
          }
        : { templateId: id };
      const html = await templatePreviewApi.preview(body);
      setPreviewHtml(html);
    } catch (e) {
      notification.error({
        message: "Error",
        description: "Error loading preview",
      });
    } finally {
      setIsPreviewLoading(false);
    }
  };

  useEffect(() => {
    if (id) {
      dispatch(getOneTemplate(id));
    }
  }, [id]);

  useEffect(() => {
    if (template?._id) {
      form.setFieldValue("html", template.html);
      form.setFieldValue("styles", template.styles);
      form.setFieldValue("script", template.script);
      loadPreview(false);
    }
  }, [template?._id]);

  const onFinish: FormProps<FieldType>["onFinish"] = (values) => {
    if (template?._id) {
      dispatch(updateTemplate(values, template?._id));
    }
  };

  return (
    <div
      style={{
        display: "flex",
        padding: 20,
        paddingTop: 10,
        flexDirection: "column",
      }}
    >
      <Breadcrumb
        items={[
          {
            href: "/templates",
            title: (
              <>
                <ArrowLeftOutlined />
                <span>Templates</span>
              </>
            ),
          },
          {
            title: `Template ${id}`,
          },
        ]}
      />
      <div style={{ marginTop: 20 }}>
        <Tag color="magenta">{template?.type.toUpperCase()}</Tag>
        {template?.palette === "dark" ? (
          <Tag color="black">{template?.palette.toUpperCase()}</Tag>
        ) : (
          <Tag color="default">{template?.palette.toUpperCase()}</Tag>
        )}
      </div>

      <div
        style={{
          display: "flex",
          flexDirection: "row",
          gap: 20,
          marginTop: 20,
          alignItems: "flex-start",
        }}
      >
        <Form
          style={{ width: "42%" }}
          form={form}
          layout="vertical"
          onFinish={onFinish}
        >
          <Form.Item<FieldType> label="HTML" name="html">
            <TextArea rows={12} />
          </Form.Item>
          <Form.Item<FieldType> label="Styles" name="styles">
            <TextArea rows={12} />
          </Form.Item>
          <Form.Item<FieldType> label="Script" name="script">
            <TextArea rows={6} />
          </Form.Item>
          <Button
            style={{ width: 150 }}
            htmlType="submit"
            loading={isTemplateUpdateInProgress}
            type="primary"
            size="middle"
          >
            Save
          </Button>
        </Form>

        <div style={{ width: "58%", position: "sticky", top: 10 }}>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 12,
              marginBottom: 8,
            }}
          >
            <Button
              icon={<ReloadOutlined />}
              loading={isPreviewLoading}
              onClick={() => loadPreview(true)}
            >
              Refresh preview
            </Button>
            <Radio.Group
              value={previewWidth}
              onChange={(e) => setPreviewWidth(e.target.value)}
            >
              <Radio.Button value="desktop">Desktop</Radio.Button>
              <Radio.Button value="mobile">Mobile</Radio.Button>
            </Radio.Group>
            <span style={{ opacity: 0.65 }}>
              preview renders with real sample-site data
            </span>
          </div>
          <div
            style={{
              border: "1px solid #555",
              borderRadius: 6,
              overflow: "auto",
              background: "#fff",
            }}
          >
            <iframe
              title="template preview"
              sandbox="allow-scripts allow-same-origin"
              srcDoc={previewHtml || "<p style='padding:20px'>Loading…</p>"}
              style={{
                width: previewWidth === "mobile" ? 390 : "100%",
                height: "75vh",
                border: 0,
                display: "block",
                margin: previewWidth === "mobile" ? "0 auto" : 0,
              }}
            />
          </div>
        </div>
      </div>
    </div>
  );
};
